/**
 * @file 学びメモのプレビューの中の mermaid のコードの欄を、図に置き換える。
 *
 * @what: プレビューの HTML が変わるたびに、言語が mermaid のコードの欄 (`pre > code.language-mermaid`)
 *   を探し、図にして置き換える。図にできないときは、もとの文字を残して上に1行の知らせを置く。
 *   配色は画面の配色 (`<html data-theme>`) に合わせる。1行目に大きさを書いた欄
 *   (`<pre data-mermaid-size>`、lib/markdown.ts) は、図の幅をその大きさにする。
 * @why: プレビューは renderMarkdown の消毒済みの HTML を innerHTML で出しており、図は出したあとに
 *   置き換えるしかない。React は同じ HTML の文字列では innerHTML を書き直さないので、置き換えは
 *   本文が変わるまで残る。
 * @failure: 図の作成の失敗は lib/mermaid.ts が null にし、ここでは知らせの1行に変える。
 */

import { useEffect, type RefObject } from "react";
import { cachedMermaidSvg, renderMermaid, type MermaidTheme } from "../lib/mermaid";

const FAILED_MESSAGE = "図にできませんでした";

function currentTheme(): MermaidTheme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "default";
}

function showDiagram(pre: HTMLElement, svg: string) {
  const figure = document.createElement("div");
  figure.className = "study-note-mermaid";
  // lib/mermaid.ts が securityLevel: strict で作った SVG である (図の中の文字は mermaid が消毒する)
  figure.innerHTML = svg;
  const size = pre.dataset.mermaidSize;
  const el = figure.querySelector("svg");
  if (size && el) {
    // mermaid が付ける max-width (もとの幅) を外して、書いた大きさにする
    el.style.width = size;
    el.style.maxWidth = "none";
    el.style.height = "auto";
  }
  pre.replaceWith(figure);
}

function showFailure(pre: HTMLElement) {
  const note = document.createElement("p");
  note.className = "study-note-mermaid-error";
  note.textContent = FAILED_MESSAGE;
  pre.before(note);
}

export function useMermaidBlocks(containerRef: RefObject<HTMLElement | null>, html: string) {
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    const blocks = Array.from(
      container.querySelectorAll<HTMLElement>("pre > code.language-mermaid"),
    );
    if (blocks.length === 0) return;
    const theme = currentTheme();
    let cancelled = false;
    for (const code of blocks) {
      const pre = code.parentElement!;
      const source = code.textContent ?? "";
      const hit = cachedMermaidSvg(source, theme);
      if (hit !== undefined) {
        showDiagram(pre, hit);
        continue;
      }
      void renderMermaid(source, theme).then((svg) => {
        // 待つ間に本文が変わったら、置き換え先は消えている
        if (cancelled || !pre.isConnected) return;
        if (svg === null) showFailure(pre);
        else showDiagram(pre, svg);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [containerRef, html]);
}
